import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box, Typography, CircularProgress, Alert, Chip, LinearProgress,
  Stack, FormControl, InputLabel, Select, MenuItem, Paper,
} from '@mui/material';
import { getBantuanMaps } from '../API/bantuan';
import { KECAMATAN } from '../datarecap/constants';

const KSB_CENTER = [-8.75, 116.85];
const KSB_BOUNDS = { minLat: -9.15, maxLat: -8.25, minLng: 116.55, maxLng: 117.35 };

const isInKSB = (lat, lng) =>
  lat >= KSB_BOUNDS.minLat && lat <= KSB_BOUNDS.maxLat
  && lng >= KSB_BOUNDS.minLng && lng <= KSB_BOUNDS.maxLng;

const WARNA_HEX = { merah: '#ef4444', kuning: '#f59e0b', hijau: '#10b981' };

const makeIcon = (hex) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="32">
    <path fill="${hex}" stroke="#111" stroke-width="1"
      d="M12 0C7.6 0 3.6 3.9 3.6 8.7c0 2.4 1 4.6 2.5 6.5L12 24l5.9-8.8c1.5-1.9 2.5-4.1 2.5-6.5C20.4 3.9 16.4 0 12 0zm0 12c-1.9 0-3.4-1.5-3.4-3.3s1.5-3.3 3.4-3.3 3.4 1.5 3.4 3.3S13.9 12 12 12z"/>
  </svg>`;
  return L.divIcon({
    className: 'exec-bantuan-marker',
    html: svg,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -30],
  });
};

const ICONS = {
  merah: makeIcon(WARNA_HEX.merah),
  kuning: makeIcon(WARNA_HEX.kuning),
  hijau: makeIcon(WARNA_HEX.hijau),
};

const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom);
  }, [center, map, zoom]);
  return null;
};

const formatRp = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '-';
  return 'Rp ' + Number(n).toLocaleString('id-ID');
};

/**
 * Peta eksekutif: marker penerima bantuan berwarna menurut progres.
 * - merah: 0%
 * - kuning: proses berjalan
 * - hijau: 100% selesai
 */
const ExecutiveBantuanMap = ({
  kecamatan: kecamatanProp,
  height = 420,
  showFilter = true,
  embedded = false,
}) => {
  const [kecamatan, setKecamatan] = useState(kecamatanProp || '');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, merah: 0, kuning: 0, hijau: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (kecamatanProp !== undefined) setKecamatan(kecamatanProp || '');
  }, [kecamatanProp]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBantuanMaps({ kecamatan: kecamatan || undefined });
      const list = (res.data?.data || []).filter((d) => {
        const lat = parseFloat(d.latitude);
        const lng = parseFloat(d.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng) && isInKSB(lat, lng);
      });
      setRows(list);
      setSummary(res.data?.summary || {
        total: list.length,
        merah: list.filter((d) => d.warna === 'merah').length,
        kuning: list.filter((d) => d.warna === 'kuning').length,
        hijau: list.filter((d) => d.warna === 'hijau').length,
      });
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.message || 'Gagal memuat peta penerima bantuan');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [kecamatan]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const center = useMemo(() => {
    if (!rows.length) return KSB_CENTER;
    const lat = rows.reduce((s, r) => s + parseFloat(r.latitude), 0) / rows.length;
    const lng = rows.reduce((s, r) => s + parseFloat(r.longitude), 0) / rows.length;
    return [lat, lng];
  }, [rows]);

  const body = (
    <>
      {showFilter && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter Kecamatan</InputLabel>
            <Select
              label="Filter Kecamatan"
              value={kecamatan}
              onChange={(e) => setKecamatan(e.target.value)}
            >
              <MenuItem value="">Semua Kecamatan</MenuItem>
              {KECAMATAN.map((k) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" sx={{ bgcolor: WARNA_HEX.merah, color: '#fff' }} label={`0%: ${summary.merah}`} />
            <Chip size="small" sx={{ bgcolor: WARNA_HEX.kuning, color: '#fff' }} label={`Proses: ${summary.kuning}`} />
            <Chip size="small" sx={{ bgcolor: WARNA_HEX.hijau, color: '#fff' }} label={`Selesai: ${summary.hijau}`} />
            <Chip size="small" variant="outlined" label={`Total peta: ${rows.length}`} />
          </Stack>
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          Tidak ada penerima bantuan dengan koordinat valid
          {kecamatan ? ` di Kecamatan ${kecamatan}` : ''}.
        </Alert>
      ) : (
        <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <MapContainer
            center={center}
            zoom={kecamatan ? 12 : 10}
            style={{ height, width: '100%' }}
            scrollWheelZoom
          >
            <MapController center={center} zoom={kecamatan ? 12 : 10} />
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {rows.map((r) => (
              <Marker
                key={r.id}
                position={[parseFloat(r.latitude), parseFloat(r.longitude)]}
                icon={ICONS[r.warna] || ICONS.merah}
              >
                <Popup>
                  <Typography variant="subtitle2" fontWeight={700}>{r.nama}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {r.JenisBantuan || '-'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {[r.desaKelurahan, r.kecamatan].filter((x) => x && x !== '-').join(', ')}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Status: <strong>{r.statusProses}</strong> · {Number(r.presentase) || 0}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Number(r.presentase) || 0)}
                    sx={{
                      mt: 0.5, height: 6, borderRadius: 1,
                      bgcolor: '#e5e7eb',
                      '& .MuiLinearProgress-bar': { bgcolor: WARNA_HEX[r.warna] || WARNA_HEX.merah },
                    }}
                  />
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Nominal: {formatRp(r.nominal)}
                  </Typography>
                  {(r.namaTFL || r.namaAgr) && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {[r.namaTFL && `TFL: ${r.namaTFL}`, r.namaAgr && `AGR: ${r.namaAgr}`].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      )}
    </>
  );

  if (embedded) return <Box>{body}</Box>;
  return (
    <Paper sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        Peta Penerima Bantuan & Progres
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Marker: merah = 0%, kuning = proses berjalan, hijau = selesai 100%. Koordinat dari verifikasi/questionnaire.
      </Typography>
      {body}
    </Paper>
  );
};

export default ExecutiveBantuanMap;
